import axios from "axios";
import { BASE_URL } from "./Constants";
import {
  getJwtTokenCookie,
  getOrchestrationHttpBaseUrl,
  handleExpiredAuthSession,
  isInvalidTokenResponse,
  shouldUseServerSessionAuth,
} from "../utils/authCookie";
// Set config defaults when creating the instance
export const api = axios.create({
  baseURL: BASE_URL,
});

const directAuthPaths = [
  "drx/api/auth/authenticate.do",
  "/drx/api/auth/authenticate.do",
  "/drx/api/change/password.do",
  "drx/api/change/password.do",
];

function isDirectDigitalRxPath(url: any): boolean {
  const path = String(url || "").trim();
  if (!shouldUseServerSessionAuth()) {
    return true;
  }
  return directAuthPaths.some((directPath) => path.startsWith(directPath));
}

function proxyUrl(url: any): string {
  const path = String(url || "").trim().replace(/^\/+/, "");
  if (!path || path.includes("://") || path.startsWith("//")) {
    throw new Error("Invalid DigitalRx proxy path");
  }
  return `${getOrchestrationHttpBaseUrl()}/auth/session/proxy/${path}`;
}

function requestUrl(url: any): string {
  return isDirectDigitalRxPath(url) ? String(url) : proxyUrl(url);
}

function requestClient(url: any) {
  return isDirectDigitalRxPath(url) ? api : axios;
}

/**
 * When a DigitalRx call fails with an expired/invalid token, kick off the login redirect.
 * Skips the auth endpoints themselves so a failed login/password change can't loop back
 * into the login screen. Safe to call on every caught error — it no-ops unless the body
 * actually says the token is invalid.
 */
function handleAuthExpiryIfNeeded(url: any, status: number, data: any): void {
  const path = String(url || "");
  const isAuthPath = directAuthPaths.some((p) => path.startsWith(p));
  if (isAuthPath) {
    return;
  }
  if (isInvalidTokenResponse(status, data)) {
    handleExpiredAuthSession();
  }
}

function requestOptions(url: any, headers: Record<string, string>) {
  const useDirect = isDirectDigitalRxPath(url);
  const token = getJwtTokenCookie();
  return {
    headers: {
      ...(useDirect && token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    withCredentials: !useDirect,
  };
}

export function authenticatedDigitalRxUrl(path: string): string {
  const cleanPath = String(path || "").trim().replace(/^\/+/, "");
  if (!cleanPath) {
    return "";
  }
  if (shouldUseServerSessionAuth()) {
    return proxyUrl(cleanPath);
  }
  return `${BASE_URL}${cleanPath}`;
}

export function authenticatedDigitalRxDownloadUrl(path: string): string {
  const url = authenticatedDigitalRxUrl(path);
  if (!url || shouldUseServerSessionAuth()) {
    return url;
  }
  const token = getJwtTokenCookie();
  if (!token) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

/**
 * Normalizes every failure path (post/put/Delete/postWithBody/get) to the same shape:
 * `{data, status}` on a handled HTTP error response (real status code from the server),
 * or `{data: null, status: 0, networkError: true}` for a network-level failure with no
 * `error.response` (timeout, DNS failure, CORS, offline, etc). Previously the five
 * helpers each had ad-hoc, inconsistent catch-block logic — `post`/`get` returned
 * `{data, status}` on a handled error but diverged on network failures (`post` returned
 * `undefined`, `get` returned the raw Axios error), while `put`/`Delete`/`postWithBody`
 * always returned the raw error object regardless of failure type. A caller receiving
 * `undefined` (e.g. after a network blip on a send/cancel/remove-prescription call) could
 * throw an uncaught TypeError deep in a promise chain with no clear signal of what
 * happened. Every caller can now safely read `.data` / `.status` regardless of the
 * failure path.
 */
function toHttpResult(error: any): { data: any; status: number; networkError?: boolean } {
  if (error && error.response && typeof error.response.status !== "undefined") {
    return {
      data: error.response.data,
      status: error.response.status,
    };
  }
  return {
    data: null,
    status: 0,
    networkError: true,
  };
}

export const post = (url: any, data = {}) => {
  return requestClient(url)
    .post(
      requestUrl(url),
      data,
      requestOptions(url, {
        "Content-Type": "application/json",
      })
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      if (error && error.response && error.response.status) {
        handleAuthExpiryIfNeeded(url, error.response.status, error.response.data);
      }
      return toHttpResult(error);
    });
};

export const postWithBody = (url: any, data = {}) => {
  return requestClient(url)
    .post(
      requestUrl(url),
      data,
      requestOptions(url, {
        Accept: "*/*",
        "Content-Type": "multipart/form-data",
      })
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      if (error && error.response && error.response.status) {
        handleAuthExpiryIfNeeded(url, error.response.status, error.response.data);
      }
      return toHttpResult(error);
    });
};

export const get = (url: any, data = {}) => {
  return requestClient(url)
    .get(
      requestUrl(url),
      requestOptions(url, {
        "Content-Type": "application/json",
      })
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      if (error && error.response && error.response.status) {
        handleAuthExpiryIfNeeded(url, error.response.status, error.response.data);
      }
      return toHttpResult(error);
    });
};

export const put = (url: any, data = {}) => {
  return requestClient(url)
    .put(
      requestUrl(url),
      data,
      requestOptions(url, {
        "Content-Type": "application/json",
      })
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      if (error && error.response && error.response.status) {
        handleAuthExpiryIfNeeded(url, error.response.status, error.response.data);
      }
      return toHttpResult(error);
    });
};

export const Delete = (url: any, data = {}) => {
  return requestClient(url)
    .delete(requestUrl(url), {
      ...requestOptions(url, {
        "Content-Type": "application/json",
      }),
      data,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      if (error && error.response && error.response.status) {
        handleAuthExpiryIfNeeded(url, error.response.status, error.response.data);
      }
      return toHttpResult(error);
    });
};
